import { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Wifi,
  PlugZap,
  Activity,
  TrendingUp,
  DollarSign,
  BatteryCharging,
  MapPin,
  ShieldCheck,
  HardDrive,
  Layers,
  BatteryMedium,
  Sun,
  Link2,
  Handshake,
  Wrench,
  Radio,
  Building2,
  ServerCrash,
  ChevronsUpDown,
} from "lucide-react";
import {
  CHART_COLORS,
  GAUGE_PRIMARY,
  GAUGE_TRACK,
  SEVERITY_COLORS,
  CHART_REGISTRY,
  CHART_PICKER_STORAGE_KEY,
} from "../fleet_dashboard/constants";
import ChartCard from "../fleet_dashboard/ChartCard";
import ChartDataTable from "../fleet_dashboard/ChartDataTable";
import CollapsibleSection from "../fleet_dashboard/CollapsibleSection";
import ChartPicker from "../fleet_dashboard/ChartPicker";
import SemiCircleGauge from "../fleet_dashboard/charts/SemiCircleGauge";
import SolidGauge from "../fleet_dashboard/charts/SolidGauge";
import RadialRing from "../fleet_dashboard/charts/RadialRing";
import FunnelChart from "../fleet_dashboard/charts/FunnelChart";
import HorizontalBarChart from "../fleet_dashboard/charts/HorizontalBarChart";
import VerticalBarChart from "../fleet_dashboard/charts/VerticalBarChart";
import CountryMap from "./charts/CountryMap";
import "./charts/CountryMap.scss";
import StatusDonutChart from "../../pages/dashboard/bkp/components/StatusDonutChart";
import BkpKpiCard from "../../pages/dashboard/bkp/components/KpiCards";
import EnergySessionsChart from "../../pages/dashboard/bkp/components/EnergySessionsChart";
import TopSites from "../../pages/dashboard/bkp/components/TopSites";
import {
  MOCK_CHARGER_STATUS_DONUT,
  MOCK_CONNECTIVITY,
  MOCK_FLEET_HEALTH,
  MOCK_COMMISSIONING,
  MOCK_OFFLINE_DURATION,
  MOCK_FW_VERSIONS,
  MOCK_FW_FRESHNESS,
  MOCK_MODEL,
  MOCK_SKU,
  MOCK_PHASE,
  MOCK_DLB,
  MOCK_PARTNER,
  MOCK_OFFGRID,
  MOCK_MODE,
  MOCK_OFF_PEAK,
  MOCK_VENDOR,
  MOCK_MAX_CURRENT,
  MOCK_ENERGY_DATA,
  MOCK_TOP_SITES,
} from "./hooks/useStatsChartData";
import useFleetStats from "./hooks/useFleetStats";
import useStatsTransform from "./hooks/useStatsTransform";
import { isEnphaseUser } from "../../lib/utils/utility";
import "../dashboard/Dashboard.scss";
import "../fleet_dashboard/FleetDashboard.scss";

const M = "(Mock)";

const SECTION_KEYS = ["operationalHealth", "siteLocations", "firmwareMaintenance", "fleetConfig", "deploymentConfig"];

export default function FleetDashboardOpenSearch() {
  const navigate = useNavigate();
  const enphaseUser = isEnphaseUser();
  // eslint-disable-next-line no-unused-vars
  const { data: statsData, isLoading: statsLoading, isError: statsError, refresh: refreshStats } = useFleetStats();

  // ── Drill-down: navigate to fleet list with a filter applied ──
  const drillDown = useCallback(
    (filterKey, filterValue) => {
      const params = new URLSearchParams();
      params.set(filterKey, String(filterValue));
      navigate(`/fleet/list?${params.toString()}`);
    },
    [navigate]
  );
  const {
    connectivityData: apiConnectivity,
    fleetHealthData: apiHealth,
    commissioningData: apiCommissioning,
    offlineDurationData: apiOfflineDuration,
    countryData: apiCountry,
    fwFreshnessData: apiFwFreshness,
    fwVersionsData: apiFwVersions,
    modelData: apiModel,
    skuData: apiSku,
    phaseConfigData: apiPhase,
    maxCurrentData: apiMaxCurrent,
    dlbData: apiDlb,
    partnerData: apiPartner,
    offGridData: apiOffGrid,
    vendorData: apiVendor,
    chargingStatusData: apiChargingStatus,
  } = useStatsTransform(statsData);

  // ── Section collapse state (all open by default for mock review) ──
  const [sectionOpen, setSectionOpen] = useState(() => SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {}));
  const toggleSection = useCallback(key => {
    setSectionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const allExpanded = SECTION_KEYS.every(k => sectionOpen[k]);
  const handleToggleAll = useCallback(() => {
    const next = !allExpanded;
    setSectionOpen(SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: next }), {}));
  }, [allExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chart visibility (reuse same localStorage key as old dashboard) ──
  const ALL_CHART_KEYS = useMemo(() => CHART_REGISTRY.map(c => c.key), []);
  const [visibleCharts, setVisibleCharts] = useState(() => {
    try {
      const stored = localStorage.getItem(CHART_PICKER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      /* ignore */
    }
    return ALL_CHART_KEYS;
  });
  const handleVisibleChartsChange = useCallback(next => {
    setVisibleCharts(next);
    localStorage.setItem(CHART_PICKER_STORAGE_KEY, JSON.stringify(next));
  }, []);
  const visibleSet = useMemo(() => new Set(visibleCharts), [visibleCharts]);
  const isVisible = useCallback(key => visibleSet.has(key), [visibleSet]);
  const hasVisibleCharts = useCallback(
    sectionKey => CHART_REGISTRY.some(c => c.section === sectionKey && visibleSet.has(c.key)),
    [visibleSet]
  );

  return (
    <div className="dashboard">
      <div className="fleet-dashboard__content fleet-dashboard__content--visible">
        {/* ═══════════════════════════════════════════════
            ALWAYS-OPEN: Time Range + KPI + Energy (Mock)
            ═══════════════════════════════════════════════ */}
        {!enphaseUser && (
          <div className="fleet-overview-panel">
            <div className="kpi-grid kpi-grid--single-row">
              <BkpKpiCard
                label={`Total Energy ${M}`}
                value={12500}
                unit="kWh"
                icon={Activity}
                iconClass="kpi-card__icon--orange"
              />
              <BkpKpiCard
                label={`Total Sessions ${M}`}
                value={842}
                icon={BatteryCharging}
                iconClass="kpi-card__icon--blue"
              />
              <BkpKpiCard
                label={`Avg Utilization ${M}`}
                value={67}
                unit="%"
                icon={TrendingUp}
                iconClass="kpi-card__icon--green"
              />
              <BkpKpiCard
                label={`Total Cost ${M}`}
                value={3200}
                prefix="$"
                icon={DollarSign}
                iconClass="kpi-card__icon--purple"
              />
              <BkpKpiCard
                label={`Utilization Time ${M}`}
                value="320h 15m"
                icon={Activity}
                iconClass="kpi-card__icon--blue"
              />
            </div>
            <div className="dashboard-charts dashboard-charts--full">
              <EnergySessionsChart data={MOCK_ENERGY_DATA} loading={false} />
            </div>
            <TopSites sites={MOCK_TOP_SITES} loading={false} />
          </div>
        )}

        {/* ── Chart Picker + Expand All / Collapse All ── */}
        <div className="fleet-section-controls">
          <ChartPicker visibleCharts={visibleCharts} onChange={handleVisibleChartsChange} />
          <button type="button" className="fleet-section-controls__btn" onClick={handleToggleAll}>
            <ChevronsUpDown size={14} />
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
        </div>

        {/* ══════════════════════════════════════════════
            SECTION 1 — OPERATIONAL HEALTH
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("operationalHealth") && (
          <CollapsibleSection
            title={
              apiChargingStatus || apiConnectivity || apiHealth || apiCommissioning || apiOfflineDuration
                ? "Operational Health"
                : `Operational Health ${M}`
            }
            icon={Activity}
            isOpen={sectionOpen.operationalHealth}
            onToggle={() => toggleSection("operationalHealth")}
          >
            {(isVisible("chargerStatus") || isVisible("connectivity") || isVisible("fleetHealth")) && (
              <div className="dashboard-charts dashboard-charts--trio">
                {isVisible("chargerStatus") && (
                  <StatusDonutChart
                    data={apiChargingStatus || MOCK_CHARGER_STATUS_DONUT}
                    title={apiChargingStatus ? "Charging Status" : `Charging Status ${M}`}
                  />
                )}
                {isVisible("connectivity") &&
                  (() => {
                    const c = apiConnectivity || { online: 178, offline: 22, total: 200, chartData: MOCK_CONNECTIVITY };
                    const title = apiConnectivity ? "Connectivity" : `Connectivity ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Wifi}
                        subtitle="Online vs Offline"
                        badge={`${c.online} / ${c.total} Online`}
                      >
                        <SemiCircleGauge
                          value={c.online}
                          total={c.total}
                          color={GAUGE_PRIMARY}
                          trackColor={GAUGE_TRACK}
                        />
                        <ChartDataTable
                          data={c.chartData}
                          nameLabel="Status"
                          valueLabel="Chargers"
                          colors={[GAUGE_PRIMARY, GAUGE_TRACK]}
                          onRowClick={item => drillDown("is_connected", item.name === "Online")}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("fleetHealth") &&
                  (() => {
                    const h = apiHealth || { healthy: 195, faulted: 5, total: 200, chartData: MOCK_FLEET_HEALTH };
                    const title = apiHealth ? "Fleet Health" : `Fleet Health ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={ShieldCheck}
                        subtitle="Healthy vs Faulted"
                        badge={`${h.healthy} healthy · ${h.faulted} faulted`}
                      >
                        <SolidGauge value={h.healthy} max={h.total} color={GAUGE_PRIMARY} trackColor={GAUGE_TRACK} />
                        <ChartDataTable
                          data={h.chartData}
                          nameLabel="Status"
                          valueLabel="Chargers"
                          colors={[GAUGE_PRIMARY, GAUGE_TRACK]}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}

            {(isVisible("commissioning") || isVisible("offlineDuration")) && (
              <div className="dashboard-charts">
                {isVisible("commissioning") &&
                  (() => {
                    const cm = apiCommissioning || {
                      commissioned: 160,
                      pending: 40,
                      total: 200,
                      chartData: MOCK_COMMISSIONING,
                    };
                    const title = apiCommissioning ? "Commissioning Status" : `Commissioning Status ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={PlugZap}
                        subtitle="Deployment pipeline"
                        badge={`${cm.total.toLocaleString()} chargers`}
                      >
                        <FunnelChart data={cm.chartData} colors={[CHART_COLORS[0], CHART_COLORS[6], CHART_COLORS[4]]} />
                        <ChartDataTable
                          data={cm.chartData}
                          nameLabel="Status"
                          valueLabel="Chargers"
                          colors={[CHART_COLORS[0], CHART_COLORS[6], CHART_COLORS[4]]}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("offlineDuration") &&
                  (() => {
                    const od = apiOfflineDuration || { total: 22, chartData: MOCK_OFFLINE_DURATION };
                    const title = apiOfflineDuration ? "Offline Duration" : `Offline Duration ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={ServerCrash}
                        subtitle="Downtime breakdown"
                        badge={`${od.total.toLocaleString()} offline`}
                      >
                        <HorizontalBarChart data={od.chartData} colors={SEVERITY_COLORS} />
                        <ChartDataTable
                          data={od.chartData}
                          nameLabel="Duration"
                          valueLabel="Chargers"
                          colors={SEVERITY_COLORS}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 2 — CHARGER GEOGRAPHY
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("siteLocations") && (
          <CollapsibleSection
            title={apiCountry ? "Charger Geography" : `Charger Geography ${M}`}
            icon={MapPin}
            isOpen={sectionOpen.siteLocations}
            onToggle={() => toggleSection("siteLocations")}
          >
            {isVisible("geoMap") &&
              (() => {
                const cd = apiCountry || {
                  counts: { US: 80, IN: 30, DE: 25, AU: 15, GB: 10 },
                  chartData: [
                    { name: "United States", value: 80 },
                    { name: "India", value: 30 },
                    { name: "Germany", value: 25 },
                    { name: "Australia", value: 15 },
                    { name: "United Kingdom", value: 10 },
                  ],
                  total: 160,
                };
                const title = apiCountry ? "Chargers by Country" : `Chargers by Country ${M}`;
                return (
                  <ChartCard
                    title={title}
                    icon={MapPin}
                    subtitle="Geographic distribution"
                    badge={`${cd.chartData.length} countries · ${cd.total.toLocaleString()} chargers`}
                  >
                    <CountryMapWithTable cd={cd} statsLoading={statsLoading} drillDown={drillDown} />
                  </ChartCard>
                );
              })()}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 3 — FIRMWARE & MAINTENANCE
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("firmwareMaintenance") && (
          <CollapsibleSection
            title={
              apiFwVersions || apiFwFreshness || apiModel || apiSku
                ? "Firmware & Maintenance"
                : `Firmware & Maintenance ${M}`
            }
            icon={HardDrive}
            isOpen={sectionOpen.firmwareMaintenance}
            onToggle={() => toggleSection("firmwareMaintenance")}
          >
            {(isVisible("fwVersions") || isVisible("fwFreshness")) && (
              <div className="dashboard-charts">
                {isVisible("fwVersions") &&
                  (() => {
                    const fv = apiFwVersions || { chartData: MOCK_FW_VERSIONS, total: 200 };
                    const title = apiFwVersions ? "Firmware Versions" : `Firmware Versions ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={HardDrive}
                        subtitle="Fleet firmware"
                        badge={`${fv.chartData.length} versions · ${fv.total.toLocaleString()} chargers`}
                      >
                        <RadialRing data={fv.chartData} colors={CHART_COLORS} centerLabel="Firmware" />
                        <ChartDataTable
                          data={fv.chartData}
                          nameLabel="Version"
                          valueLabel="Chargers"
                          colors={CHART_COLORS}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("fwFreshness") &&
                  (() => {
                    const fw = apiFwFreshness || { chartData: MOCK_FW_FRESHNESS, total: 200 };
                    const title = apiFwFreshness ? "Firmware Freshness" : `Firmware Freshness ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Wrench}
                        subtitle="Update recency"
                        badge={`${fw.total.toLocaleString()} chargers`}
                      >
                        <VerticalBarChart data={fw.chartData} colors={SEVERITY_COLORS} />
                        <ChartDataTable
                          data={fw.chartData}
                          nameLabel="Freshness"
                          valueLabel="Chargers"
                          colors={SEVERITY_COLORS}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}

            {(isVisible("chargerModel") || isVisible("skuDistribution")) && (
              <div className="dashboard-charts">
                {isVisible("chargerModel") &&
                  (() => {
                    const md = apiModel || { chartData: MOCK_MODEL, total: 200 };
                    const title = apiModel ? "Chargers by Model" : `Chargers by Model ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={PlugZap}
                        subtitle="Model breakdown"
                        badge={`${md.chartData.length} models · ${md.total.toLocaleString()} chargers`}
                      >
                        <HorizontalBarChart data={md.chartData} colors={CHART_COLORS} />
                        <ChartDataTable
                          data={md.chartData}
                          nameLabel="Model"
                          valueLabel="Chargers"
                          colors={CHART_COLORS}
                          onRowClick={item => drillDown("model_name", item.rawKey || item.name)}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("skuDistribution") &&
                  (() => {
                    const sk = apiSku || { chartData: MOCK_SKU, total: 200 };
                    const title = apiSku ? "SKU Distribution" : `SKU Distribution ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Radio}
                        subtitle="SKU breakdown"
                        badge={`${sk.chartData.length} SKUs · ${sk.total.toLocaleString()} chargers`}
                      >
                        <HorizontalBarChart data={sk.chartData} colors={CHART_COLORS} />
                        <ChartDataTable
                          data={sk.chartData}
                          nameLabel="SKU"
                          valueLabel="Chargers"
                          colors={CHART_COLORS}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 4 — FLEET CONFIGURATION
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("fleetConfig") && (
          <CollapsibleSection
            title={apiPhase || apiDlb || apiPartner || apiOffGrid ? "Fleet Configuration" : `Fleet Configuration ${M}`}
            icon={Layers}
            isOpen={sectionOpen.fleetConfig}
            onToggle={() => toggleSection("fleetConfig")}
          >
            {(isVisible("phaseConfig") || isVisible("dlbAdoption")) && (
              <div className="dashboard-charts">
                {isVisible("phaseConfig") &&
                  (() => {
                    const ph = apiPhase || { chartData: MOCK_PHASE, total: 200 };
                    const title = apiPhase ? "Phase Configuration" : `Phase Configuration ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Layers}
                        subtitle="Phase Distribution"
                        badge={`${ph.chartData.length} types · ${ph.total.toLocaleString()} chargers`}
                      >
                        <RadialRing data={ph.chartData} colors={CHART_COLORS} centerLabel="Phase" />
                        <ChartDataTable
                          data={ph.chartData}
                          nameLabel="Phase"
                          valueLabel="Chargers"
                          colors={CHART_COLORS}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("dlbAdoption") &&
                  (() => {
                    const dl = apiDlb || { chartData: MOCK_DLB, total: 200 };
                    const title = apiDlb ? "DLB Adoption" : `DLB Adoption ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Link2}
                        subtitle="Load balancing"
                        badge={`${dl.total.toLocaleString()} chargers`}
                      >
                        <RadialRing data={dl.chartData} colors={[CHART_COLORS[0], CHART_COLORS[6]]} centerLabel="DLB" />
                        <ChartDataTable
                          data={dl.chartData}
                          nameLabel="Status"
                          valueLabel="Chargers"
                          colors={[CHART_COLORS[0], CHART_COLORS[6]]}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}

            {(isVisible("partnerIntegration") || isVisible("offGridStatus")) && (
              <div className="dashboard-charts">
                {isVisible("partnerIntegration") &&
                  (() => {
                    const pt = apiPartner || { chartData: MOCK_PARTNER, total: 200 };
                    const title = apiPartner ? "Partner Integration" : `Partner Integration ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Handshake}
                        subtitle="Integration coverage"
                        badge={`${pt.chartData.length} partners · ${pt.total.toLocaleString()} chargers`}
                      >
                        <RadialRing
                          data={pt.chartData}
                          colors={[CHART_COLORS[1], CHART_COLORS[6]]}
                          centerLabel="Partners"
                        />
                        <ChartDataTable
                          data={pt.chartData}
                          nameLabel="Partner"
                          valueLabel="Chargers"
                          colors={[CHART_COLORS[1], CHART_COLORS[6]]}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("offGridStatus") &&
                  (() => {
                    const og = apiOffGrid || { chartData: MOCK_OFFGRID, total: 200 };
                    const title = apiOffGrid ? "Off-Grid Status" : `Off-Grid Status ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Sun}
                        subtitle="Grid connectivity"
                        badge={`${og.total.toLocaleString()} chargers`}
                      >
                        <RadialRing
                          data={og.chartData}
                          colors={[CHART_COLORS[4], CHART_COLORS[0]]}
                          centerLabel="Grid"
                        />
                        <ChartDataTable
                          data={og.chartData}
                          nameLabel="Status"
                          valueLabel="Chargers"
                          colors={[CHART_COLORS[4], CHART_COLORS[0]]}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 5 — DEPLOYMENT & CONFIG
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("deploymentConfig") && (
          <CollapsibleSection
            title={apiVendor || apiMaxCurrent ? "Deployment & Config" : `Deployment & Config ${M}`}
            icon={BatteryMedium}
            isOpen={sectionOpen.deploymentConfig}
            onToggle={() => toggleSection("deploymentConfig")}
          >
            {(isVisible("chargingMode") || isVisible("offPeakEligibility")) && (
              <div className="dashboard-charts">
                {isVisible("chargingMode") && (
                  <ChartCard
                    title={`Charging Mode ${M}`}
                    icon={BatteryMedium}
                    subtitle="Mode distribution"
                    badge="3 modes"
                  >
                    <FunnelChart data={MOCK_MODE} colors={[CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[2]]} />
                    <ChartDataTable
                      data={MOCK_MODE}
                      nameLabel="Mode"
                      valueLabel="Chargers"
                      colors={[CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[2]]}
                    />
                  </ChartCard>
                )}
                {isVisible("offPeakEligibility") && (
                  <ChartCard
                    title={`Off-Peak Eligibility ${M}`}
                    icon={Sun}
                    subtitle="Fleet eligibility"
                    badge="200 chargers"
                  >
                    <SolidGauge
                      value={130}
                      max={200}
                      label="Eligible Chargers"
                      color={GAUGE_PRIMARY}
                      trackColor={GAUGE_TRACK}
                    />
                    <ChartDataTable
                      data={MOCK_OFF_PEAK}
                      nameLabel="Eligibility"
                      valueLabel="Chargers"
                      colors={[GAUGE_PRIMARY, GAUGE_TRACK]}
                    />
                  </ChartCard>
                )}
              </div>
            )}

            {(isVisible("vendorDistribution") || isVisible("maxCurrent")) && (
              <div className="dashboard-charts">
                {isVisible("vendorDistribution") &&
                  (() => {
                    const vd = apiVendor || { chartData: MOCK_VENDOR, total: 200 };
                    const title = apiVendor ? "Vendor Distribution" : `Vendor Distribution ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={Building2}
                        subtitle="Manufacturer split"
                        badge={`${vd.chartData.length} vendors · ${vd.total.toLocaleString()} chargers`}
                      >
                        <HorizontalBarChart data={vd.chartData} colors={CHART_COLORS} />
                        <ChartDataTable
                          data={vd.chartData}
                          nameLabel="Vendor"
                          valueLabel="Chargers"
                          colors={CHART_COLORS}
                          onRowClick={item => drillDown("vendor_name", item.rawKey || item.name)}
                        />
                      </ChartCard>
                    );
                  })()}
                {isVisible("maxCurrent") &&
                  (() => {
                    const mc = apiMaxCurrent || { chartData: MOCK_MAX_CURRENT, total: 200 };
                    const title = apiMaxCurrent ? "Max Current Distribution" : `Max Current Distribution ${M}`;
                    return (
                      <ChartCard
                        title={title}
                        icon={PlugZap}
                        subtitle="Fleet power ratings"
                        badge={`${mc.chartData.length} ratings · ${mc.total.toLocaleString()} chargers`}
                      >
                        <HorizontalBarChart data={mc.chartData} colors={CHART_COLORS} />
                        <ChartDataTable
                          data={mc.chartData}
                          nameLabel="Rating"
                          valueLabel="Chargers"
                          colors={CHART_COLORS}
                        />
                      </ChartCard>
                    );
                  })()}
              </div>
            )}
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}

/**
 * Pairs CountryMap + ChartDataTable with shared hover state.
 * Table row hover highlights the corresponding map bubble and vice versa.
 */
function CountryMapWithTable({ cd, statsLoading, drillDown }) {
  const [highlightedCode, setHighlightedCode] = useState(null);

  // Map bubble hover → find matching table row index
  const highlightedIndex = useMemo(() => {
    if (!highlightedCode) return null;
    const idx = cd.chartData.findIndex(d => d.rawKey === highlightedCode);
    return idx >= 0 ? idx : null;
  }, [highlightedCode, cd.chartData]);

  // Table row hover → extract country code and highlight map bubble
  const handleRowHover = useCallback(item => {
    setHighlightedCode(item ? item.rawKey || null : null);
  }, []);

  return (
    <>
      <CountryMap
        countryCounts={cd.counts}
        loading={statsLoading}
        highlightedCode={highlightedCode}
        onHighlight={setHighlightedCode}
      />
      <ChartDataTable
        data={cd.chartData}
        nameLabel="Country"
        valueLabel="Chargers"
        colors={CHART_COLORS}
        onRowClick={item => drillDown("country", item.apiKeys?.[0] || item.rawKey || item.name)}
        onRowHover={handleRowHover}
        highlightedIndex={highlightedIndex}
      />
    </>
  );
}

CountryMapWithTable.propTypes = {
  cd: PropTypes.shape({
    counts: PropTypes.object.isRequired,
    chartData: PropTypes.array.isRequired,
  }).isRequired,
  statsLoading: PropTypes.bool,
  drillDown: PropTypes.func.isRequired,
};
